import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  inject,
  input,
  signal,
} from '@angular/core';

import { AgGridModule } from 'ag-grid-angular';
import { GridReadyEvent } from 'ag-grid-community';
import { GridReadyEvent as PGridReadyEvent, RowModelType } from 'ag-grid-enterprise';

import { GridViewType, IDataService, IForcedFilter } from '../../models/types';
import { GridMetaData } from '../../services/dto/metadata';
import { BaseGridComponent } from '../base-grid.component';
import { GridTableComponent, GridTableRowSize } from '../grid-table/grid-table.component';
import { PaginationComponent } from '../pagination/pagination.component';
import { NoRowsOverlayComponent } from './no-rows/no-rows-overlay.component';

@Component({
  host: {
    '[class.is-fill]': 'fill()',
    '[class.is-grid-view]': "displayType() === 'grid'",
  },
  imports: [CommonModule, AgGridModule, GridTableComponent, PaginationComponent],
  selector: 'cam-ag-grid',
  styleUrl: './ag-grid.component.scss',
  templateUrl: './ag-grid.component.html',
})
export class AgGridComponent<T extends { id: string }>
  extends BaseGridComponent<T>
  implements OnInit, OnChanges, AfterViewInit
{
  cardTemplate = input<TemplateRef<{ items: T[] }>>();

  colsMetaData = input<GridMetaData[] | null>(null);
  data = input<T[]>();

  defaultDisplay = input<GridViewType>('card');

  forcedFilter = input<IForcedFilter>({});

  services = input<IDataService<T> | null>(null);

  fill = input(false);

  rowSize = input<GridTableRowSize>('md');

  public readonly paginationAutoPageSize = signal(false);
  public readonly noRowsOverlayComponent = NoRowsOverlayComponent;

  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** En vue `card` et `table`, AG Grid reste monté (il porte l'état : colonnes, filtres, tri, pages) mais invisible. */
  get gridHeight(): string | null {
    if (this.displayType() !== 'grid') {
      return '0px';
    }
    return this.fill() ? null : 'auto';
  }

  get displayType() {
    return this._grid.displayType;
  }

  override ngOnInit() {
    super.ngOnInit();

    if (this.services()) {
      // Server-side : la vue `card` (alimentée par `filteredData$`, local uniquement) n'est pas possible ;
      // la vue `table` lit les nœuds AG Grid et fonctionne, donc `defaultDisplay="table"` est honoré.
      this._grid.switchView(this.defaultDisplay() === 'table' ? 'table' : 'grid', false);
    } else {
      this._grid.switchViewDefault(this.defaultDisplay());
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && !changes['data'].firstChange) {
      this._grid.updateData(this.data() ?? []);
    }
  }

  ngAfterViewInit() {
    this.overrideGridOptions();
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this._grid.destroy();
  }

  public isReady(event: GridReadyEvent<unknown>) {
    super.onGridReady(event as unknown as PGridReadyEvent);

    // Les popups AG Grid (filtres de colonne, menu contextuel) sortent du conteneur de la grille :
    // en vue `table` celui-ci est à 0px / overflow hidden, un filtre ancré sur `cam-grid-table` y
    // serait invisible. Le host est `position: relative` (voir le SCSS) pour servir de référentiel.
    event.api.setGridOption('popupParent', this._host.nativeElement);

    this._grid.setData({
      colsMetaData: this.colsMetaData(),
      data: this.data() ?? [],
      fieldsOverrides: this.fieldsOverrides(),
      forcedFilter: this.forcedFilter(),
      options: {
        ...this.gridOptions(),
        ...{
          colOptions: { ...this.colOptions(), editable: false },
          contextMenuItems: this.contextMenuItems(),
          domLayout: this.fill() ? 'normal' : 'autoHeight',
          openByDefault: this.openByDefault(),
          services: this.services(),
          treeData: this.treeData(),
        },
      },
      showHierarchyLabel: false,
    });
  }

  public getRowModelType(): RowModelType {
    return 'serverSide';
  }

  private overrideGridOptions() {
    this.paginationAutoPageSize.set(
      this.fill() && !!this.gridOptions().pagination && !this.gridOptions().paginationPageSize
    );
    this.gridOptions.set({
      ...this.gridOptions(),
      suppressDragLeaveHidesColumns: true,
    });
  }
}
